<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import AcoesPasso from '../components/AcoesPasso.vue';
import DicaDestaque from '../components/DicaDestaque.vue';
import {
  BTN,
  BTN_LINK,
  BTN_SECONDARY,
  BTN_SMALL,
  CARD,
  CHIP,
  CHIP_TONES,
  ENTER,
  H1,
  MOTION,
  SMALL,
  SUB,
  TILE,
  TILE_TONES,
} from '../ui';

// Imóveis do catálogo da conta para conferir. "Conferido" fica no progresso
// do onboarding; a IA segue lendo o catálogo como sempre.
const props = defineProps({
  state: { type: Object, required: true },
  properties: { type: Object, default: null },
  accountId: { type: [Number, String], required: true },
});
const emit = defineEmits(['next', 'save']);

const { t } = useI18n();
const filter = ref('all');

const items = computed(() => props.properties?.items || []);
const checked = computed(
  () => new Set(props.state.progress?.checked_properties || [])
);
const isChecked = item => checked.value.has(item.code);
const pending = computed(() => items.value.filter(item => !isChecked(item)));
const checkedCount = computed(() => items.value.length - pending.value.length);
const percent = computed(() =>
  items.value.length ? (checkedCount.value / items.value.length) * 100 : 0
);
const visible = computed(() => {
  if (filter.value === 'pending') return pending.value;
  if (filter.value === 'checked') return items.value.filter(isChecked);
  return items.value;
});
const missingCatalog = computed(() => Boolean(props.properties?.missing_catalog));
const failed = computed(() => Boolean(props.properties?.error));

const saveChecked = codes =>
  emit('save', { checked_properties: [...new Set(codes)] });
const confirm = item => saveChecked([...checked.value, item.code]);
const undo = item =>
  saveChecked([...checked.value].filter(code => code !== item.code));
const confirmAll = () =>
  saveChecked([...checked.value, ...pending.value.map(item => item.code)]);

const priceOf = item =>
  item.price_label ||
  (item.price ? item.price.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }) : null);

const place = item =>
  [item.neighborhood, item.city].filter(Boolean).join(', ');

const FILTERS = computed(() => [
  { key: 'all', n: items.value.length },
  { key: 'pending', n: pending.value.length },
  { key: 'checked', n: checkedCount.value },
]);

const tips = computed(() => [
  t('ONBOARDING_VISOR.PROPERTIES.TIPS.NO_PRICE'),
  t('ONBOARDING_VISOR.PROPERTIES.TIPS.NEW_ONES'),
  t('ONBOARDING_VISOR.PROPERTIES.TIPS.SOLD'),
]);
</script>

<template>
  <div>
    <section :class="ENTER">
      <h1 :class="H1">
        <span class="text-n-slate-10">{{ t('ONBOARDING_VISOR.PROPERTIES.HELLO') }}</span>
        <br />
        {{
          items.length
            ? t('ONBOARDING_VISOR.PROPERTIES.TITLE', { n: properties.total || items.length })
            : t('ONBOARDING_VISOR.PROPERTIES.TITLE_EMPTY')
        }}
      </h1>
      <p :class="SUB">{{ t('ONBOARDING_VISOR.PROPERTIES.SUBTITLE') }}</p>
    </section>

    <div v-if="!properties" class="mt-8 flex items-center gap-3 text-n-slate-10">
      <Spinner /> {{ t('ONBOARDING_VISOR.PROPERTIES.LOADING') }}
    </div>

    <section
      v-else-if="missingCatalog || failed || !items.length"
      :class="CARD"
      class="mt-8 max-w-[40rem]"
      data-testid="conectar-catalogo"
    >
      <div class="flex items-center gap-3 mb-3">
        <span :class="[TILE, TILE_TONES.aviso]"><Icon icon="i-ph-buildings-duotone" /></span>
        <h2 class="text-xl font-medium tracking-[-0.02em]">
          {{ failed ? t('ONBOARDING_VISOR.PROPERTIES.FAILED_TITLE') : t('ONBOARDING_VISOR.PROPERTIES.CONNECT_TITLE') }}
        </h2>
      </div>
      <p class="text-n-slate-11">
        {{ failed ? t('ONBOARDING_VISOR.PROPERTIES.FAILED_TEXT') : t('ONBOARDING_VISOR.PROPERTIES.CONNECT_TEXT') }}
      </p>
      <router-link
        :to="{ name: 'ai_settings', params: { accountId, category: 'comentarios', section: 'catalogo' } }"
        class="mt-4"
        :class="[BTN_SECONDARY, BTN_SMALL]"
      >
        {{ t('ONBOARDING_VISOR.PROPERTIES.CONNECT') }}
      </router-link>
    </section>

    <div v-else class="grid gap-6 mt-8 lg:grid-cols-3 items-start max-md:gap-4 max-md:mt-5">
      <section :class="CARD" class="lg:col-span-2 self-stretch flex flex-col gap-[1.125rem] justify-center">
        <div class="inline-flex self-start bg-n-slate-3 rounded-full p-1 gap-0.5 max-md:self-stretch max-md:overflow-x-auto" role="tablist">
          <button
            v-for="option in FILTERS"
            :key="option.key"
            type="button"
            role="tab"
            :aria-selected="filter === option.key"
            class="px-4 py-2 rounded-full text-sm whitespace-nowrap max-md:px-3"
            :class="[MOTION, filter === option.key ? 'bg-n-card text-n-slate-12 font-medium' : 'text-n-slate-11']"
            @click="filter = option.key"
          >
            {{ t(`ONBOARDING_VISOR.PROPERTIES.FILTERS.${option.key.toUpperCase()}`, { n: option.n }) }}
          </button>
        </div>
        <div>
          <div class="flex justify-between mb-2" :class="SMALL">
            <span>{{ t('ONBOARDING_VISOR.PROPERTIES.CHECKED') }}</span>
            <b class="text-n-slate-12 font-medium" data-testid="conferidos">
              {{ t('ONBOARDING_VISOR.PROPERTIES.CHECKED_OF', { n: checkedCount, total: items.length }) }}
            </b>
          </div>
          <div class="h-2 rounded-full bg-n-slate-3 overflow-hidden">
            <i class="block h-full rounded-full bg-n-blue-9" :class="MOTION" :style="{ width: `${percent}%` }" />
          </div>
        </div>
        <button v-if="pending.length" type="button" class="self-start" :class="[BTN_SECONDARY, BTN_SMALL]" data-testid="conferir-todos" @click="confirmAll">
          {{ t('ONBOARDING_VISOR.PROPERTIES.CHECK_ALL', { n: pending.length }) }}
        </button>
      </section>
      <DicaDestaque :title="t('ONBOARDING_VISOR.PROPERTIES.TIPS_TITLE')" :tips="tips" />

      <article
        v-for="item in visible"
        :key="item.code"
        :class="[CARD, !priceOf(item) && !isChecked(item) ? '!border-n-amber-9' : '']"
        class="!p-0 overflow-hidden"
        data-testid="imovel"
      >
        <div class="relative aspect-[300/160] bg-n-slate-3">
          <img v-if="item.photo" :src="item.photo" alt="" class="w-full h-full object-cover" />
          <Icon v-else icon="i-ph-house-line-duotone" class="absolute inset-0 m-auto size-10 text-n-slate-10" />
          <span class="absolute left-3 top-3" :class="[CHIP, isChecked(item) ? CHIP_TONES.ok : 'bg-n-card text-n-blue-11']">
            <Icon :icon="isChecked(item) ? 'i-ph-check-circle-duotone' : 'i-ph-globe-duotone'" class="size-3.5" />
            {{ isChecked(item) ? t('ONBOARDING_VISOR.PROPERTIES.BADGE_CHECKED') : t('ONBOARDING_VISOR.PROPERTIES.BADGE_IMPORTED') }}
          </span>
        </div>
        <div class="px-5 pt-[1.125rem] pb-5">
          <h3 class="font-medium">{{ item.title }}</h3>
          <p :class="SMALL">{{ place(item) }}</p>
          <div class="flex gap-4 mt-3 mb-1.5 text-sm text-n-slate-11">
            <span v-if="item.bedrooms" class="flex gap-1.5 items-center">
              <Icon icon="i-ph-bed-duotone" class="size-[1.125rem] text-n-slate-10" />
              {{ t('ONBOARDING_VISOR.PROPERTIES.BEDROOMS', { n: item.bedrooms }, item.bedrooms) }}
            </span>
            <span v-if="item.garage" class="flex gap-1.5 items-center">
              <Icon icon="i-ph-car-duotone" class="size-[1.125rem] text-n-slate-10" />
              {{ t('ONBOARDING_VISOR.PROPERTIES.GARAGE', { n: item.garage }, item.garage) }}
            </span>
          </div>
          <p v-if="priceOf(item)" class="text-xl font-medium tracking-[-0.02em] mt-1">{{ priceOf(item) }}</p>
          <div v-else class="flex items-start gap-2 mt-2.5 text-[0.8125rem] text-n-amber-11" data-testid="sem-preco">
            <Icon icon="i-ph-warning-duotone" class="size-4 flex-none mt-0.5" />
            {{ t('ONBOARDING_VISOR.PROPERTIES.NO_PRICE') }}
          </div>
          <div class="flex gap-2 mt-4">
            <template v-if="isChecked(item)">
              <button type="button" :class="[BTN_LINK, BTN_SMALL]" @click="undo(item)">
                {{ t('ONBOARDING_VISOR.PROPERTIES.UNDO') }}
              </button>
            </template>
            <template v-else>
              <button type="button" :class="[BTN, BTN_SMALL]" data-testid="esta-certo" @click="confirm(item)">
                <Icon icon="i-ph-check-circle-duotone" class="size-5" />
                {{ t('ONBOARDING_VISOR.PROPERTIES.CORRECT') }}
              </button>
              <a
                v-if="item.url"
                :href="item.url"
                target="_blank"
                rel="noopener noreferrer"
                :class="[BTN_SECONDARY, BTN_SMALL]"
              >
                {{ t('ONBOARDING_VISOR.PROPERTIES.FIX') }}
              </a>
            </template>
          </div>
        </div>
      </article>
    </div>

    <AcoesPasso :trust="t('ONBOARDING_VISOR.PROPERTIES.TRUST')">
      <button type="button" :class="BTN" data-testid="seguir" @click="emit('next')">
        {{ t('ONBOARDING_VISOR.PROPERTIES.NEXT') }}
        <Icon icon="i-ph-arrow-right-duotone" class="size-5" />
      </button>
      <span v-if="pending.length" :class="SMALL" class="max-md:hidden">
        {{ t('ONBOARDING_VISOR.PROPERTIES.LATER', { n: pending.length }) }}
      </span>
    </AcoesPasso>
  </div>
</template>
